import { useEffect, useRef, useState } from 'react';
import { handled, type Route, type Stop } from '@day';
import { useStore } from '../app/store';
import { frame, hide, openCourier, openRoute, openStop, setShare, ui, type UI } from '../app/ui';
import { sharedNow, type SharedView } from '../app/sharing';
import { planOrder, planRuns, planTimed } from '../app/actions';
import { contextMenu } from '../app/menu';
import { courierFirst, dayTime, streetOf, time, two } from '../app/words';
import { mapId } from '../firebase';
import { keyOf, routeTitle, type Model, type Person } from '../data/model';
import { Icon } from '../ui/Icon';
import { photoFailed, photoToTry } from '../ui/photos';
import { decode, loadMaps, onAuthFailure, type MapsState } from './google';
import { escape, icon, ll, makeHtml, mapOptions, styleFor, type HtmlOverlay, type LL } from './overlay';

/**
 * Live's map: Google's, in the app's own style (assets/map_styles), with each courier in a colour of their own, their
 * stops as plates with the stop number, the part of a route behind them faded, a waiting route dashed
 * (control-room-web.html, "Google's map"). Google's lines come from the route records (the phone asked for them); the
 * map asks Google for nothing else.
 */

/** Where a stop's plate shows: Google's position, else where the courier marked it. */
const spotOf = (s: Stop) => s.spot ?? s.handledAt;

/** A route's path through its stops: Google's line when the record has one, else straight from stop to stop. */
function pathOf(r: Route): LL[] {
  if (r.polyline) return decode(r.polyline);
  const pts = r.stops.map(spotOf).filter((p): p is { lat: number; lng: number } => !!p).map(ll);
  return r.start && r.state === 'waiting' ? [ll(r.start), ...pts] : pts;
}

/** The path cut where the courier is: behind them (faded) and ahead. */
function cut(path: LL[], here: LL | null, r: Route): { behind: LL[]; ahead: LL[] } {
  if (!path.length) return { behind: [], ahead: [] };
  if (r.state !== 'running') return { behind: path, ahead: [] };
  if (!here) {
    // No position: up to the last stop handled.
    const lastDone = r.stops.filter(handled).at(-1);
    const target = lastDone ? spotOf(lastDone) : null;
    if (!target) return { behind: [], ahead: path };
    here = ll(target);
  }
  let best = 0;
  let bestD = Infinity;
  path.forEach((p, i) => {
    const d = (p.lat - here!.lat) ** 2 + (p.lng - here!.lng) ** 2;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  // For straight lines, the courier is between the last stop handled and the next.
  if (!r.polyline) {
    const i = Math.max(0, Math.min(r.current, path.length));
    return { behind: [...path.slice(0, i), here], ahead: [here, ...path.slice(i)] };
  }
  return { behind: [...path.slice(0, best + 1), here], ahead: [here, ...path.slice(best + 1)] };
}

const dashes = (color: string, opacity: number): google.maps.IconSequence[] => [
  { icon: { path: 'M 0,-1 0,1', strokeOpacity: opacity, strokeColor: color, scale: 3 }, offset: '0', repeat: '12px' },
];

interface Drawn {
  lines: Map<string, { behind: google.maps.Polyline; ahead: google.maps.Polyline }>;
  pins: Map<string, HtmlOverlay>;
  discs: Map<string, HtmlOverlay>;
  areas: Map<string, google.maps.Circle>;
  shop: HtmlOverlay | null;
  /** A new route's stops, and the straight line through them (no request to Google while planning). */
  plan: HtmlOverlay[];
  planLine: google.maps.Polyline | null;
  /** The spot Who's closest measures to. */
  spot: HtmlOverlay | null;
  /** Stops being shared out: each courier's route, its stops, and the zones under them. */
  share: { lines: Map<string, google.maps.Polyline>; pins: Map<string, HtmlOverlay>; zones: Map<string, google.maps.Polygon> };
}

const fresh = (): Drawn => ({ lines: new Map(), pins: new Map(), discs: new Map(), areas: new Map(), shop: null, plan: [], planLine: null, spot: null, share: { lines: new Map(), pins: new Map(), zones: new Map() } });

/**
 * A courier's face on their disc: their photo when they've added one, ringed in their colour (as `Av` in the lists);
 * the initial when there's none or it won't load, trying the photo again after a while (photos.ts; the map is drawn
 * again at least every 15 s). Changed only when the link changes, so a redraw doesn't flicker.
 */
function face(el: HTMLDivElement, photo: string | undefined, initial: string): boolean {
  const want = photoToTry(photo) ?? '';
  const key = `${want}|${initial}`;
  if (el.dataset.face !== key) {
    el.dataset.face = key;
    if (want) {
      const img = document.createElement('img');
      img.alt = '';
      img.draggable = false;
      img.onerror = () => {
        photoFailed(want);
        el.dataset.face = `|${initial}`;
        el.classList.remove('pic');
        el.textContent = initial;
      };
      img.src = want;
      el.replaceChildren(img);
    } else el.textContent = initial;
  }
  return !!want;
}

export function MapPane({ m }: { m: Model }) {
  const s = useStore(ui);
  const shared = useStore(sharedNow).view;
  const box = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const drawn = useRef<Drawn>(fresh());
  const [state, setState] = useState<MapsState>({ state: 'loading' });
  const [satellite, setSatellite] = useState(false);
  const latest = useRef({ m, s, shared });
  latest.current = { m, s, shared };

  useEffect(() => {
    let alive = true;
    onAuthFailure((why) => alive && setState({ state: 'failed', why }));
    loadMaps().then(
      () => {
        if (!alive || !box.current) return;
        const map = new google.maps.Map(box.current, mapOptions(ui.get().theme));
        mapRef.current = map;
        // Right-click a spot: Who's closest, a new route there, its coordinates.
        map.addListener('contextmenu', (e: google.maps.MapMouseEvent) => {
          const ev = e.domEvent as MouseEvent | undefined;
          ev?.preventDefault?.();
          if (!e.latLng || !ev) return;
          contextMenu(`spot:${e.latLng.lat()},${e.latLng.lng()}`, ev.clientX, ev.clientY);
        });
        box.current.addEventListener('contextmenu', (e) => e.preventDefault());
        setState({ state: 'ready' });
        google.maps.event.addListenerOnce(map, 'idle', () => frame());
      },
      (e: Error) => alive && setState({ state: 'failed', why: e.message }),
    );
    return () => {
      alive = false;
      const d = drawn.current;
      for (const l of d.lines.values()) {
        l.behind.setMap(null);
        l.ahead.setMap(null);
      }
      for (const p of [...d.pins.values(), ...d.discs.values()]) p.overlay.setMap(null);
      for (const c of d.areas.values()) c.setMap(null);
      d.shop?.overlay.setMap(null);
      for (const o of d.plan) o.overlay.setMap(null);
      d.planLine?.setMap(null);
      d.spot?.overlay.setMap(null);
      for (const l of d.share.lines.values()) l.setMap(null);
      for (const p of d.share.pins.values()) p.overlay.setMap(null);
      for (const z of d.share.zones.values()) z.setMap(null);
      drawn.current = fresh();
      mapRef.current = null;
    };
  }, []);

  // The theme: the app's light or dark style.
  useEffect(() => {
    if (state.state === 'ready' && !mapId) mapRef.current?.setOptions({ styles: styleFor(s.theme) });
  }, [s.theme, state.state]);

  useEffect(() => {
    if (state.state === 'ready') mapRef.current?.setMapTypeId(satellite ? 'hybrid' : 'roadmap');
  }, [satellite, state.state]);

  // Draw what's on the map: every change to the day, and the view.
  useEffect(() => {
    const map = mapRef.current;
    if (state.state !== 'ready' || !map) return;
    draw(map, drawn.current, m, s);
    drawShare(map, drawn.current, m, s, s.view === 'share' ? shared : null);
  }, [m, s, shared, state.state]);

  // The first answer when sharing out: framed.
  const sharing = s.view === 'share' && !!shared;
  useEffect(() => {
    const map = mapRef.current;
    if (state.state === 'ready' && map && sharing) fit(map, latest.current.m, latest.current.s, latest.current.shared);
  }, [sharing, state.state]);

  // Frame what's showing when asked (a route opened, F, back to today).
  useEffect(() => {
    const map = mapRef.current;
    if (state.state !== 'ready' || !map) return;
    fit(map, latest.current.m, latest.current.s, latest.current.shared);
  }, [s.frame, state.state]);

  // A stop's record opening or closing changes the map's width: frame what's being looked at again.
  useEffect(() => {
    const el = box.current;
    const map = mapRef.current;
    if (state.state !== 'ready' || !el || !map) return;
    let width = el.offsetWidth;
    let timer = 0;
    const seen = new ResizeObserver(() => {
      if (el.offsetWidth === width) return;
      width = el.offsetWidth;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fit(map, latest.current.m, latest.current.s, latest.current.shared), 60);
    });
    seen.observe(el);
    return () => {
      seen.disconnect();
      window.clearTimeout(timer);
    };
  }, [state.state]);

  // On a small window the record lies over the map instead: frame what's left when it opens.
  const recordOpen = s.view === 'stop';
  useEffect(() => {
    const map = mapRef.current;
    if (state.state === 'ready' && map && recordOpen && recordCovers()) fit(map, latest.current.m, latest.current.s, latest.current.shared);
  }, [recordOpen, state.state]);

  // Stepping through the stops: the one picked stays on screen.
  useEffect(() => {
    const map = mapRef.current;
    const picked = s.view === 'stop' && s.stop ? latest.current.m.stop(s.stop) : null;
    const at = picked ? spotOf(picked) : null;
    if (state.state === 'ready' && map && at && map.getBounds() && !map.getBounds()!.contains(at)) map.panTo(ll(at));
  }, [s.view, s.stop, state.state]);

  return (
    <div className="mapwrap">
      <div id="map" ref={box} />
      {state.state === 'failed' && (
        <div className="mapnote" role="status">
          <Icon name="mapTrifold" size="lg" />
          <b>Google’s map isn’t showing here</b>
          <span className="cap">{state.why}</span>
          <span className="cap">Everything else works: the list, the numbers and the records.</span>
        </div>
      )}
      <div className="mapctl">
        <button data-tip="Show everyone · F" data-side="left" aria-label="Show everyone" onClick={() => frame()}>
          <Icon name="arrowsOut" />
        </button>
        <button data-tip="Zoom in" data-side="left" aria-label="Zoom in" onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() ?? 13) + 1)}>
          <Icon name="plus" />
        </button>
        <button data-tip="Zoom out" data-side="left" aria-label="Zoom out" onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() ?? 13) - 1)}>
          <Icon name="minus" />
        </button>
        <button data-tip={satellite ? 'Map' : 'Satellite'} data-side="left" aria-label="Satellite" aria-pressed={satellite} onClick={() => setSatellite(!satellite)}>
          <Icon name="stack" />
        </button>
      </div>
      {s.view === 'day' && !(s.page === 'routes' && s.rday && s.rday < m.today) && (
        <div className="maplegend" id="legend">
          {m.routes.map((r) => {
            const p = m.person(r.riderId);
            const off = s.hidden.has(r.id);
            return (
              <button
                key={r.id}
                className="chip"
                data-ctx={`route:${r.id}`}
                data-tip={`${off ? 'Show' : 'Hide'} ${routeTitle(r, m.zone)} on the map`}
                aria-pressed={!off}
                style={off ? { opacity: 0.45 } : undefined}
                onClick={() => hide(r.id)}
              >
                <span className={`swatch ${r.state === 'running' ? 'running' : r.state === 'waiting' || r.state === 'declined' ? 'waiting' : 'done'}`} style={{ '--c': p?.hex ?? '#5E594F' } as React.CSSProperties} />
                {p?.first ?? 'A courier'} · {routeTitle(r, m.zone)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function pinHtml(s: Stop, p: Person | null): string {
  const label =
    s.state === 'done'
      ? icon('check')
      : s.state === 'failed'
        ? icon(s.reason === 'anotherDay' ? 'calendarBlank' : 'x')
        : s.state === 'left'
          ? icon('minus')
          : `${s.n}${s.type === 'pickup' ? icon('arrowUp') : ''}`;
  const cls = s.state === 'left' ? 'failed' : s.state;
  return `<div class="p" style="--c:${p?.hex ?? '#5E594F'};--on:${p?.on ?? '#fff'}">${label}</div><span hidden>${cls}</span>`;
}

function draw(map: google.maps.Map, d: Drawn, m: Model, s: UI) {
  const ink3 = getComputedStyle(document.documentElement).getPropertyValue('--ink-tertiary').trim() || '#9A9488';
  const focus = s.view === 'route' || s.view === 'stop' ? s.route : null;
  // A route under the pointer in Routes' list: the others fade as when one is open, and a hidden one shows meanwhile.
  const lit = focus ?? (s.page === 'routes' && s.view === 'day' ? s.hover : null);
  const personView = s.view === 'person' ? s.person : null;
  const planning = s.view === 'plan' || s.view === 'closest' || s.view === 'share';
  const near = new Set(s.view === 'closest' ? m.people.filter((p) => p.day?.running?.position || p.day?.presence).map((p) => p.uid) : []);
  const seenLines = new Set<string>();
  const seenPins = new Set<string>();
  const seenDiscs = new Set<string>();
  const seenAreas = new Set<string>();

  // The shop: where routes start.
  if (m.shop) {
    if (!d.shop) {
      const el = document.createElement('div');
      el.className = 'shop';
      d.shop = makeHtml(map, ll(m.shop), el, 30);
    }
    d.shop.el.innerHTML = `${icon('storefront')}${escape(m.place?.label ?? m.business?.name ?? m.shop.label)}`;
    d.shop.el.dataset.tip = `Where routes start${m.shop.address ? ` · ${m.shop.address}` : ''}`;
    d.shop.move(ll(m.shop));
  } else if (d.shop) {
    d.shop.overlay.setMap(null);
    d.shop = null;
  }

  // A route opened that isn't today's (still open from an earlier day, ended after midnight) is drawn while it's open.
  // A past day on the Routes page shows only that day's route, once opened: today's would read as that day's.
  const extra = focus ? m.route(focus) : null;
  const pastDay = s.page === 'routes' && !!s.rday && s.rday < m.today;
  const drawn = pastDay ? (extra ? [extra] : []) : extra && !m.routes.includes(extra) ? [...m.routes, extra] : m.routes;
  for (const r of drawn) {
    const p = m.person(r.riderId);
    const colour = p?.hex ?? '#5E594F';
    const gone = s.hidden.has(r.id) && lit !== r.id;
    const dim = planning || (!!lit && lit !== r.id) || (!!personView && personView !== r.riderId);
    const path = pathOf(r);
    const here = r.state === 'running' && r.position ? ll(r.position) : null;
    let line = d.lines.get(r.id);
    if (!line) {
      line = {
        behind: new google.maps.Polyline({ map, zIndex: 1, clickable: true }),
        ahead: new google.maps.Polyline({ map, zIndex: 2, clickable: true }),
      };
      line.behind.addListener('click', () => openRoute(r.id));
      line.ahead.addListener('click', () => openRoute(r.id));
      d.lines.set(r.id, line);
    }
    seenLines.add(r.id);
    if (r.state === 'waiting' || r.state === 'declined') {
      line.behind.setOptions({ path: [], visible: false });
      line.ahead.setOptions({ path, visible: !gone, strokeOpacity: 0, icons: dashes(colour, dim ? 0.25 : 0.9) });
    } else {
      const { behind, ahead } = cut(path, here, r);
      line.behind.setOptions({ path: behind, visible: !gone, strokeColor: colour, strokeOpacity: dim ? 0.1 : 0.35, strokeWeight: dim ? 4 : 5, icons: [] });
      line.ahead.setOptions({ path: ahead, visible: !gone, strokeColor: colour, strokeOpacity: dim ? 0.22 : 1, strokeWeight: dim ? 4 : 5, icons: [] });
    }
    for (const stop of r.stops) {
      const at = spotOf(stop);
      if (!at) continue;
      const k = keyOf(stop);
      seenPins.add(k);
      let pin = d.pins.get(k);
      const z = stop.state === 'current' ? 16 : handled(stop) ? 10 : 12;
      if (!pin) {
        const el = document.createElement('div');
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          openStop(k);
        });
        pin = makeHtml(map, ll(at), el, z);
        d.pins.set(k, pin);
      }
      const cls = stop.state === 'left' ? 'failed' : stop.state;
      pin.el.className = `pin ${cls}${dim ? ' dim' : ''}${s.view === 'stop' && s.stop === k ? ' picked' : ''}`;
      pin.el.dataset.ctx = `stop:${k}`;
      pin.el.dataset.tip = `${two(stop.n)} · ${streetOf(stop)}`;
      const html = pinHtml(stop, p);
      if (pin.el.dataset.html !== html) {
        pin.el.innerHTML = html;
        pin.el.dataset.html = html;
      }
      pin.el.style.display = gone ? 'none' : '';
      pin.el.style.zIndex = String(s.view === 'stop' && s.stop === k ? 40 : z);
      pin.move(ll(at));
    }
    // The courier on their route: a disc in their colour, where their phone last said.
    if (r.state === 'running' && here && p?.day?.running?.id === r.id) {
      const k = `run:${r.riderId}`;
      seenDiscs.add(k);
      let disc = d.discs.get(k);
      if (!disc) {
        const el = document.createElement('div');
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          openRoute(latest(m, r.riderId) ?? r.id);
        });
        disc = makeHtml(map, here, el, 25);
        d.discs.set(k, disc);
      }
      const quiet = p.day?.quiet;
      const pic = face(disc.el, p.courier.photoUrl, p.first.slice(0, 1).toUpperCase());
      disc.el.className = `cdisc${quiet ? ' quiet' : ''}${dim || planning ? ' dim' : ''}${near.has(p.uid) ? ' near' : ''}${pic ? ' pic' : ''}`;
      disc.el.style.setProperty('--c', colour);
      disc.el.style.setProperty('--on', p.on);
      disc.el.dataset.ctx = `route:${r.id}`;
      disc.el.dataset.tip = `${p.first} · ${routeTitle(r, m.zone)}${r.position?.at ? ` · ${time(r.position.at, m.zone)}` : ''}`;
      disc.el.style.display = gone ? 'none' : '';
      disc.move(here);
    }
  }

  // Couriers not on a route: their rough area while Routr is open (about 500 m across).
  for (const p of m.people) {
    const area = p.day?.presence;
    if (!area || p.day?.running) continue;
    const k = `area:${p.uid}`;
    seenAreas.add(k);
    seenDiscs.add(k);
    let c = d.areas.get(k);
    if (!c) {
      c = new google.maps.Circle({ map, radius: 260, clickable: false });
      d.areas.set(k, c);
    }
    // An area older than 2 hours fades (CourierArea.freshFor): where they were, not where they are.
    const stale = area.at == null || m.now - area.at > 2 * 60 * 60 * 1000;
    c.setOptions({ center: ll(area), fillColor: ink3, fillOpacity: stale ? 0.05 : 0.12, strokeColor: ink3, strokeOpacity: stale ? 0.25 : 0.5, strokeWeight: 1.5 });
    let disc = d.discs.get(k);
    if (!disc) {
      const el = document.createElement('div');
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        openCourier(p.uid);
      });
      disc = makeHtml(map, ll(area), el, 20);
      d.discs.set(k, disc);
    }
    const pic = face(disc.el, p.courier.photoUrl, courierFirst(p.courier).slice(0, 1).toUpperCase());
    disc.el.className = `cdisc quiet${stale || lit || planning || (personView && personView !== p.uid) ? ' dim' : ''}${near.has(p.uid) ? ' near' : ''}${pic ? ' pic' : ''}`;
    disc.el.style.setProperty('--c', p.hex);
    disc.el.style.setProperty('--on', p.on);
    disc.el.dataset.ctx = `courier:${p.uid}`;
    disc.el.dataset.tip = `${p.first} · rough area${area.at ? `, seen ${dayTime(area.at, m.now, m.zone)}` : ''}`;
    disc.move(ll(area));
  }

  drawPlan(map, d, m, s);
  for (const [k, l] of d.lines) if (!seenLines.has(k)) (l.behind.setMap(null), l.ahead.setMap(null), d.lines.delete(k));
  for (const [k, p] of d.pins) if (!seenPins.has(k)) (p.overlay.setMap(null), d.pins.delete(k));
  for (const [k, p] of d.discs) if (!seenDiscs.has(k)) (p.overlay.setMap(null), d.discs.delete(k));
  for (const [k, c] of d.areas) if (!seenAreas.has(k)) (c.setMap(null), d.areas.delete(k));
}

const latest = (m: Model, uid: string) => m.person(uid)?.day?.running?.id ?? null;

/** A new route's stops as dark plates, numbered, with a straight dashed line; Who's closest's spot as a pin. */
function drawPlan(map: google.maps.Map, d: Drawn, m: Model, s: UI) {
  // Split into runs: run by run, each a loop from where the courier loads, numbered as the list numbers them.
  const runs = s.view === 'plan' && s.plan ? planRuns(m, s.plan) : null;
  const byId = new Map((s.plan?.stops ?? []).map((x) => [x.id, x]));
  const stops = runs ? planTimed(m, s.plan!).order.map((id) => byId.get(id)!).filter(Boolean) : s.view === 'plan' && s.plan ? planOrder(m, s.plan) : [];
  while (d.plan.length > stops.length) d.plan.pop()!.overlay.setMap(null);
  stops.forEach((x, i) => {
    let o = d.plan[i];
    if (!o) {
      const el = document.createElement('div');
      el.className = 'pin new';
      o = makeHtml(map, ll(x), el, 18);
      d.plan[i] = o;
    }
    o.el.innerHTML = `<div class="p">${i + 1}</div>`;
    o.el.dataset.tip = `${two(i + 1)} on the new route`;
    o.move(ll(x));
  });
  const depot = runs ? ll(runs.depot) : null;
  const path = runs
    ? runs.runs.flatMap((run, i) => {
        const ids = new Set(run.map((x) => x.id));
        const mine = stops.filter((x) => ids.has(x.id)).map(ll);
        return [depot!, ...mine, ...(i < runs.runs.length - 1 || s.plan?.back ? [depot!] : [])];
      })
    : stops.length
      ? [...(m.shop ? [ll(m.shop)] : []), ...stops.map(ll)]
      : [];
  if (path.length > 1) {
    const ink = getComputedStyle(document.documentElement).getPropertyValue('--ink').trim() || '#15130F';
    if (!d.planLine) d.planLine = new google.maps.Polyline({ map, zIndex: 3, clickable: false, strokeOpacity: 0 });
    d.planLine.setOptions({ path, icons: dashes(ink, 0.8), visible: true });
  } else d.planLine?.setOptions({ visible: false });
  const at = s.view === 'closest' && s.closest ? ll(s.closest.at) : null;
  if (at) {
    if (!d.spot) {
      const el = document.createElement('div');
      el.className = 'pin new picked';
      el.innerHTML = `<div class="p">${icon('mapPinFill')}</div>`;
      el.dataset.tip = 'This spot';
      d.spot = makeHtml(map, at, el, 35);
    }
    d.spot.move(at);
  } else if (d.spot) {
    d.spot.overlay.setMap(null);
    d.spot = null;
  }
}

/**
 * Stops being shared out: each courier's route dashed in their colour from the start through its stops, as the list
 * numbers them (Google's line once Sort all asked for it), stops left out as neutral plates, and the zones under them.
 * Click a stop to pick it (and open its courier's list); right-click to move it.
 */
function drawShare(map: google.maps.Map, d: Drawn, m: Model, s: UI, v: SharedView | null) {
  const seenLines = new Set<string>();
  const seenPins = new Set<string>();
  const seenZones = new Set<string>();
  if (v) {
    for (const z of m.zones) {
      seenZones.add(z.id);
      let poly = d.share.zones.get(z.id);
      if (!poly) {
        poly = new google.maps.Polygon({ map, clickable: false, zIndex: 0 });
        d.share.zones.set(z.id, poly);
      }
      poly.setOptions({ paths: z.path.map(ll), strokeColor: z.hex, strokeOpacity: 0.55, strokeWeight: 1.5, fillColor: z.hex, fillOpacity: 0.07 });
    }
    const start = m.shop ? ll(m.shop) : null;
    for (const r of v.routes) {
      if (!r.ids.length) continue;
      seenLines.add(r.uid);
      let line = d.share.lines.get(r.uid);
      if (!line) {
        line = new google.maps.Polyline({ map, zIndex: 6, clickable: false, strokeOpacity: 0 });
        d.share.lines.set(r.uid, line);
      }
      const sorted = s.share?.sorted[r.uid];
      const path = r.google && sorted?.polyline ? decode(sorted.polyline) : [...(start ? [start] : []), ...r.ids.map((id) => v.at.get(id)).filter((p): p is { lat: number; lng: number } => !!p).map(ll)];
      const dim = !!s.share?.open && s.share.open !== r.uid;
      line.setOptions({ path, icons: dashes(r.p.hex, dim ? 0.3 : 0.95), visible: true });
      r.ids.forEach((id, i) => {
        const at = v.at.get(id);
        if (!at) return;
        seenPins.add(id);
        const pin = sharePin(map, d, id, at);
        const picked = s.sel.has(`share:${id}`);
        pin.el.className = `pin share${dim ? ' dim' : ''}${picked ? ' picked' : ''}${r.warn.has(id) ? ' warned' : ''}`;
        pin.el.dataset.tip = `${two(i + 1)} · ${r.p.first} · ${m.waiting.find((w) => w.id === id)?.street ?? ''}${r.warn.has(id) ? ` · ${r.warn.get(id)}` : ''}`;
        pin.el.dataset.uid = r.uid;
        const html = `<div class="p" style="--c:${r.p.hex};--on:${r.p.on}">${i + 1}</div>`;
        if (pin.el.dataset.html !== html) {
          pin.el.innerHTML = html;
          pin.el.dataset.html = html;
        }
        pin.el.style.zIndex = String(picked ? 40 : dim ? 14 : 22);
        pin.move(ll(at));
      });
    }
    for (const x of v.left) {
      const at = v.at.get(x.w.id);
      if (!at) continue;
      seenPins.add(x.w.id);
      const pin = sharePin(map, d, x.w.id, at);
      pin.el.className = `pin share left${s.sel.has(`share:${x.w.id}`) ? ' picked' : ''}`;
      pin.el.dataset.tip = `${x.w.street} · left out: right-click to give it to someone`;
      pin.el.dataset.uid = '';
      const html = `<div class="p">${icon('minus')}</div>`;
      if (pin.el.dataset.html !== html) {
        pin.el.innerHTML = html;
        pin.el.dataset.html = html;
      }
      pin.move(ll(at));
    }
  }
  for (const [k, l] of d.share.lines) if (!seenLines.has(k)) (l.setMap(null), d.share.lines.delete(k));
  for (const [k, p] of d.share.pins) if (!seenPins.has(k)) (p.overlay.setMap(null), d.share.pins.delete(k));
  for (const [k, z] of d.share.zones) if (!seenZones.has(k)) (z.setMap(null), d.share.zones.delete(k));
}

/** A shared-out stop's plate: click picks it and opens its courier's list; right-click moves it. */
function sharePin(map: google.maps.Map, d: Drawn, id: string, at: { lat: number; lng: number }): HtmlOverlay {
  let pin = d.share.pins.get(id);
  if (pin) return pin;
  const el = document.createElement('div');
  el.dataset.ctx = `sharestop:${id}`;
  el.addEventListener('click', (e) => {
    e.stopPropagation();
    const uid = el.dataset.uid;
    if (uid) setShare(() => ({ open: uid }));
    ui.set((x) => {
      const next = new Set(x.sel);
      if (next.has(`share:${id}`)) next.delete(`share:${id}`);
      else next.add(`share:${id}`);
      return { sel: next };
    });
  });
  pin = makeHtml(map, ll(at), el, 22);
  d.share.pins.set(id, pin);
  return pin;
}

/** How much of the map a stop's record covers: none, unless the window is too small for it to sit beside the map. */
function recordCovers(): number {
  const pane = document.querySelector<HTMLElement>('.pane');
  return pane && getComputedStyle(pane).position === 'absolute' ? pane.offsetWidth : 0;
}

/** Frames what's showing: a route, a courier, or everyone not done (control-room-web.html `frame`). */
function fit(map: google.maps.Map, m: Model, s: UI, shared: SharedView | null = null) {
  const pts: LL[] = [];
  const add = (p: { lat: number; lng: number } | null | undefined) => p && pts.push(ll(p));
  if (s.view === 'share' && shared) {
    add(m.shop);
    for (const p of shared.at.values()) add(p);
  } else if ((s.view === 'route' || s.view === 'stop') && s.route) {
    const r = m.route(s.route);
    r?.stops.forEach((x) => add(spotOf(x)));
    if (r?.state === 'running') add(r.position);
    if (r?.state === 'waiting') add(r.start);
  } else if (s.view === 'person' && s.person) {
    add(m.person(s.person)?.day?.presence);
    add(m.shop);
  } else if (s.view === 'plan' && s.plan) {
    add(m.shop);
    s.plan.stops.forEach(add);
    if (!s.plan.stops.length) for (const p of m.people) add(p.day?.running?.position ?? p.day?.presence);
  } else if (s.view === 'closest' && s.closest) {
    add(s.closest.at);
    for (const p of m.people) add(p.day?.running?.position ?? p.day?.presence);
  } else {
    add(m.shop);
    for (const r of m.routes) {
      if (s.hidden.has(r.id) || r.state === 'done' || r.state === 'ended') continue;
      r.stops.filter((x) => !handled(x)).forEach((x) => add(spotOf(x)));
      if (r.state === 'running') add(r.position);
    }
    for (const p of m.people) if (p.day?.presence && !p.day.running) add(p.day.presence);
    if (pts.length < 2) for (const r of m.routes) r.stops.forEach((x) => add(spotOf(x)));
  }
  if (!pts.length) return;
  if (pts.length < 2) {
    map.panTo(pts[0]);
    map.setZoom(15);
    return;
  }
  const b = new google.maps.LatLngBounds();
  pts.forEach((p) => b.extend(p));
  const corner = parseInt(getComputedStyle(document.getElementById('main') ?? document.body).getPropertyValue('--corner')) || 0;
  map.fitBounds(b, { top: 70, left: 60 + recordCovers(), right: 90, bottom: Math.max(80, corner) });
  // A few stops close together still show the streets around them.
  google.maps.event.addListenerOnce(map, 'idle', () => {
    if ((map.getZoom() ?? 0) > 15) map.setZoom(15);
  });
}
